/**
 * pageFrameOpen — should a page's framed box open its overflow while a grid
 * gesture is live?
 *
 * The page frame is overflow:hidden at rest (it's the "browser window" visual:
 * rounded corners, content clipped at the page bounds). But a dragged widget
 * follows the pointer as a REAL element inside its source grid until the drop
 * preview crosses into another grid — near the page edges that element was
 * getting cropped by the frame (the SectionWidget card got the same treatment
 * for selection rings in #71; this is the page-level twin). Open the frame
 * while a drag involves THIS page's grids — its body grid, a section grid
 * under it, or the shared header/footer bands (which render on every page) —
 * and clip normally at rest.
 */
import { FOOTER_GRID_ID, HEADER_GRID_ID } from "./sharedRegions.js";

export interface PageFrameInteraction {
  /** 2.0: the gesture union — only kind + gridId are read here. */
  gesture: { kind: string; gridId?: string | null };
  crossGrid?: {
    fromGridId: string;
    overGridId: string | null;
  } | null;
}

export function pageFrameOpen(
  interaction: PageFrameInteraction,
  parentGridIdOf: (gridId: string) => string | undefined,
  pageGridId: string,
): boolean {
  const belongs = (gid: string | null | undefined): boolean => {
    if (!gid) return false;
    if (gid === pageGridId) return true;
    if (gid === HEADER_GRID_ID || gid === FOOTER_GRID_ID) return true;
    return parentGridIdOf(gid) === pageGridId;
  };
  const gesture = interaction.gesture;
  const activeGridId =
    gesture.kind === "drag" || gesture.kind === "resize"
      ? (gesture.gridId ?? null)
      : null;
  return (
    belongs(activeGridId) ||
    belongs(interaction.crossGrid?.fromGridId) ||
    belongs(interaction.crossGrid?.overGridId)
  );
}
